/** Raiz da aplicação: roteamento entre Login (Tela 1), Comercial (Fase B), Administração (Tela 20), Auditoria (Tela 21) e Minha conta. */
import { useEffect } from 'react';
import { useSessao } from './sessao.tsx';
import { navegar, useRota } from './rotas.ts';
import { Shell } from './componentes/Shell.tsx';
import { TelaLogin } from './telas/Login.tsx';
import { ABAS_ADMIN, TelaAdministracao } from './telas/Administracao.tsx';
import { TelaAuditoria } from './telas/Auditoria.tsx';
import { TelaMinhaConta } from './telas/MinhaConta.tsx';
import { Cliente360, ListaClientes } from './comercial/Clientes.tsx';
import { NovoCliente } from './comercial/NovoCliente.tsx';
import { ContatoDetalhe, Contatos } from './comercial/Contatos.tsx';
import { ListaParceiros, ParceiroDetalhe } from './comercial/Parceiros.tsx';
import { Produtos } from './comercial/Produtos.tsx';
import { ParametrosAuditoria } from './comercial/ParametrosAuditoria.tsx';
import { Oportunidades } from './comercial/Oportunidades.tsx';
import { Oportunidade } from './comercial/Oportunidade.tsx';
import { Tarefas } from './comercial/Atividades.tsx';
import { Inicio, veFinanceiroNoInicio } from './comercial/Inicio.tsx';
import { ProvedorSituacao } from './comercial/filtros.tsx';
import { TelaProposta } from './comercial/proposta/TelaProposta.tsx';
import { ListaPedidos, TelaPedido } from './comercial/Pedidos.tsx';
import { ListaOrdens, TelaOrdem } from './comercial/Ordens.tsx';
import { ResultadosBusca } from './componentes/BuscaGlobal.tsx';
import { TelaLixeira } from './comercial/Lixeira.tsx';
import { FichaObrigacao, ListaContasPagar } from './financeiro/ContasPagar.tsx';
import { TelaConciliacao } from './financeiro/Conciliacao.tsx';
import { FichaAplicacao, TelaAplicacoes } from './financeiro/Aplicacoes.tsx';
import { TelaFluxoCaixa } from './financeiro/FluxoCaixa.tsx';
import { TelaLegado } from './financeiro/ReceberLegado.tsx';
import { FichaComissao, TelaContasReceber } from './financeiro/ContasReceber.tsx';
import { FichaAvulso, FichaNota, TelaCreditos, TelaNotas } from './financeiro/ReceberDocumentos.tsx';

export function App() {
  const { eu } = useSessao();
  // A situação escolhida nas Oportunidades vale só nesta sessão (nova sessão volta para "Em aberto").
  return (
    <ProvedorSituacao key={eu?.id ?? 'anonimo'}>
      <Rotas />
    </ProvedorSituacao>
  );
}

function Rotas() {
  const { eu, carregando, aviso, pode } = useSessao();
  const rota = useRota();
  const caminho = rota.split('?')[0];

  const primeiraAba = ABAS_ADMIN.find((a) => pode('visualizar', a.entidade));
  // Com acesso ao Comercial, o Início é a primeira tela (DEMO-03). Pacote 5: quem vê só o Financeiro abre o Início pelo
  // menu (a tela de abertura dos demais perfis não muda).
  const inicial = pode('visualizar', 'oportunidade')
    ? '/inicio'
    : primeiraAba
    ? `/administracao/${primeiraAba.rota}`
    : pode('visualizar', 'log_auditoria')
      ? '/auditoria'
      : '/minha-conta';

  useEffect(() => {
    if (eu && (caminho === '/' || caminho === '/login')) navegar(inicial);
  }, [eu, caminho, inicial]);

  // Abertura: o logo oficial da INTTRIX (arquivo do kit da marca, sem alteração) enquanto a sessão é conferida
  if (carregando)
    return (
      <div className="abertura" role="status" aria-label="Carregando o INTTRIX HUB">
        <img className="logo-inttrix" src="marca/inttrix-logo-horizontal.png" alt="INTTRIX Technology" width={320} height={100} />
        <span className="nome-produto">HUB</span>
        <span className="abertura-carregando">
          <span className="girando" aria-hidden /> Carregando…
        </span>
      </div>
    );
  if (!eu) return <TelaLogin avisoInicial={aviso} />;

  const partes = caminho.split('/').filter(Boolean);
  const semPermissao = <p className="vazio">Você não tem permissão para ver esta página.</p>;
  if (partes[0] === 'clientes') {
    const trilha = [{ rotulo: 'Comercial' }, { rotulo: 'Clientes', rota: '/clientes' }, ...(partes[1] === 'novo' ? [{ rotulo: 'Novo cliente' }] : partes[1] ? [{ rotulo: 'Cliente' }] : [])];
    const tela = !pode('visualizar', 'cliente') ? semPermissao : partes[1] === 'novo' ? (pode('criar', 'cliente') ? <NovoCliente /> : semPermissao) : partes[1] ? <Cliente360 id={partes[1]} /> : <ListaClientes />;
    return (
      <Shell rota={caminho} trilha={trilha}>
        <div key={caminho}>{tela}</div>
      </Shell>
    );
  }
  if (partes[0] === 'inicio') {
    return (
      <Shell rota={caminho} trilha={['Início']}>
        {pode('visualizar', 'oportunidade') || veFinanceiroNoInicio(pode) ? <Inicio /> : semPermissao}
      </Shell>
    );
  }
  if (partes[0] === 'tarefas') {
    return (
      <Shell rota={caminho} trilha={[{ rotulo: 'Início', rota: '/inicio' }, 'Tarefas']}>
        {pode('visualizar', 'tarefa') ? <Tarefas consulta={new URLSearchParams(rota.split('?')[1] ?? '')} /> : semPermissao}
      </Shell>
    );
  }
  if (partes[0] === 'oportunidades' && partes[2] === 'propostas' && partes[3]) {
    const numero = Number(partes[5]) || undefined;
    const pdf = partes[4] === 'pdf';
    const trilha = [{ rotulo: 'Comercial' }, { rotulo: 'Oportunidades', rota: '/oportunidades' }, { rotulo: 'Oportunidade', rota: `/oportunidades/${partes[1]}` }, { rotulo: 'Proposta', rota: `/oportunidades/${partes[1]}/propostas/${partes[3]}/v/${numero ?? ''}` }, ...(pdf ? [{ rotulo: 'PDF' }] : [])];
    const tela = !pode('visualizar', 'proposta') ? semPermissao : <TelaProposta oportunidadeId={partes[1]} propostaId={partes[3]} numero={numero} pdf={pdf} />;
    return (
      <Shell rota={caminho} trilha={trilha}>
        <div key={caminho}>{tela}</div>
      </Shell>
    );
  }
  if (partes[0] === 'ordens') {
    const numero = partes[1] ? decodeURIComponent(partes[1]) : '';
    const pdf = partes[2] === 'pdf';
    const trilha = [{ rotulo: 'Comercial' }, { rotulo: 'Ordens', rota: '/ordens' }, ...(numero ? [{ rotulo: 'Ordem', rota: `/ordens/${numero}` }] : []), ...(pdf ? [{ rotulo: 'PDF' }] : [])];
    const tela = !pode('visualizar', 'ordem') ? semPermissao : numero ? <TelaOrdem numero={numero} pdf={pdf} /> : <ListaOrdens />;
    return (
      <Shell rota={caminho} trilha={trilha}>
        <div key={caminho}>{tela}</div>
      </Shell>
    );
  }
  if (partes[0] === 'contas-pagar') {
    const numero = partes[1] ? decodeURIComponent(partes[1]) : '';
    const trilha = [{ rotulo: 'Financeiro' }, { rotulo: 'Contas a Pagar', rota: '/contas-pagar' }, ...(numero ? [{ rotulo: 'Obrigação' }] : [])];
    const tela = !pode('visualizar', 'conta_pagar') ? semPermissao : numero ? <FichaObrigacao numero={numero} /> : <ListaContasPagar />;
    return (
      <Shell rota={caminho} trilha={trilha}>
        <div key={caminho}>{tela}</div>
      </Shell>
    );
  }
  if (partes[0] === 'contas-receber') {
    const sub = partes[1] ?? '';
    const numero = partes[2] ? decodeURIComponent(partes[2]) : '';
    const rotulo = sub === 'avulsos' ? 'Recebível avulso' : sub === 'notas' ? (numero ? 'Nota da Inttrix' : 'Notas da Inttrix') : sub === 'creditos' ? 'Créditos do pagador' : sub === 'legado' ? 'Completar operações antigas' : sub ? 'Comissão' : '';
    const trilha = [{ rotulo: 'Financeiro' }, { rotulo: 'Contas a Receber', rota: '/contas-receber' }, ...(rotulo ? [{ rotulo }] : [])];
    const tela = !pode('visualizar', 'conta_receber') ? (
      semPermissao
    ) : sub === 'avulsos' && numero ? (
      <FichaAvulso numero={numero} />
    ) : sub === 'notas' ? (
      numero ? <FichaNota numero={numero} /> : <TelaNotas />
    ) : sub === 'creditos' ? (
      <TelaCreditos />
    ) : sub === 'legado' ? (
      pode('completar_legado', 'conta_receber') ? <TelaLegado /> : semPermissao
    ) : /^RAV-/i.test(sub) ? (
      <FichaAvulso numero={decodeURIComponent(sub)} />
    ) : /^NFI-/i.test(sub) ? (
      <FichaNota numero={decodeURIComponent(sub)} />
    ) : sub ? (
      <FichaComissao numero={decodeURIComponent(sub)} />
    ) : (
      <TelaContasReceber />
    );
    return (
      <Shell rota={caminho} trilha={trilha}>
        <div key={caminho}>{tela}</div>
      </Shell>
    );
  }
  if (partes[0] === 'conciliacao') {
    const trilha = [{ rotulo: 'Financeiro' }, { rotulo: 'Conciliação bancária' }];
    const tela = !pode('visualizar', 'conciliacao') ? semPermissao : <TelaConciliacao />;
    return (
      <Shell rota={caminho} trilha={trilha}>
        <div key={caminho}>{tela}</div>
      </Shell>
    );
  }
  if (partes[0] === 'fluxo-caixa') {
    // E2–E4: cada parte respeita a permissão da sua origem (sem nenhuma, sem acesso)
    const trilha = [{ rotulo: 'Financeiro' }, { rotulo: 'Fluxo de caixa' }];
    const tela = !(pode('visualizar', 'conta_pagar') || pode('visualizar', 'conta_receber') || pode('visualizar', 'conciliacao')) ? semPermissao : <TelaFluxoCaixa />;
    return (
      <Shell rota={caminho} trilha={trilha}>
        <div key={partes.slice(0, 2).join('/')}>{tela}</div>
      </Shell>
    );
  }
  if (partes[0] === 'aplicacoes') {
    // E1 (0055; D13): item próprio do Financeiro; ver = visualizar:conciliacao
    const numero = partes[1] ? decodeURIComponent(partes[1]) : '';
    const trilha = [{ rotulo: 'Financeiro' }, { rotulo: 'Aplicações', rota: '/aplicacoes' }, ...(numero ? [{ rotulo: 'Aplicação' }] : [])];
    const tela = !pode('visualizar', 'conciliacao') ? semPermissao : numero ? <FichaAplicacao numero={numero} /> : <TelaAplicacoes />;
    return (
      <Shell rota={caminho} trilha={trilha}>
        <div key={caminho}>{tela}</div>
      </Shell>
    );
  }
  if (partes[0] === 'pedidos') {
    const trilha = [{ rotulo: 'Comercial' }, { rotulo: 'Pedidos', rota: '/pedidos' }, ...(partes[1] ? [{ rotulo: 'Pedido' }] : [])];
    const tela = !pode('visualizar', 'pedido') ? semPermissao : partes[1] ? <TelaPedido numero={decodeURIComponent(partes[1])} /> : <ListaPedidos />;
    return (
      <Shell rota={caminho} trilha={trilha}>
        <div key={caminho}>{tela}</div>
      </Shell>
    );
  }
  if (partes[0] === 'oportunidades') {
    const detalhe = partes[1] && partes[1] !== 'lista' ? partes[1] : '';
    const trilha = [{ rotulo: 'Comercial' }, { rotulo: 'Oportunidades', rota: '/oportunidades' }, { rotulo: detalhe ? 'Oportunidade' : partes[1] === 'lista' ? 'Lista' : 'Kanban' }];
    const tela = !pode('visualizar', 'oportunidade') ? semPermissao : detalhe ? <Oportunidade id={detalhe} /> : <Oportunidades visao={partes[1] === 'lista' ? 'lista' : 'kanban'} />;
    return (
      <Shell rota={caminho} trilha={trilha}>
        <div key={caminho}>{tela}</div>
      </Shell>
    );
  }
  if (partes[0] === 'contatos') {
    const trilha = [{ rotulo: 'Comercial' }, { rotulo: 'Contatos', rota: '/contatos' }, ...(partes[1] ? [{ rotulo: 'Contato' }] : [])];
    const tela = !pode('visualizar', 'contato') ? semPermissao : partes[1] ? <ContatoDetalhe id={partes[1]} /> : <Contatos />;
    return (
      <Shell rota={caminho} trilha={trilha}>
        <div key={caminho}>{tela}</div>
      </Shell>
    );
  }
  if (partes[0] === 'administracao' && partes[1] === 'parametros-comerciais') {
    return (
      <Shell rota={caminho} trilha={[{ rotulo: 'Administração' }, { rotulo: 'Parâmetros e auditoria' }]}>
        {pode('visualizar', 'parametro') ? <ParametrosAuditoria /> : semPermissao}
      </Shell>
    );
  }
  if (partes[0] === 'administracao' && partes[1] === 'produtos') {
    return (
      <Shell rota={caminho} trilha={[{ rotulo: 'Administração' }, { rotulo: 'Cadastros base' }, { rotulo: 'Produtos e Serviços' }]}>
        {pode('visualizar', 'produto') ? <Produtos /> : semPermissao}
      </Shell>
    );
  }
  if (partes[0] === 'administracao' && (partes[1] === 'parceiros' || partes[1] === 'fornecedores')) {
    // "fornecedores" permanece como endereço antigo: abre Parceiros comerciais (DEMO-03).
    const trilha = [{ rotulo: 'Administração' }, { rotulo: 'Cadastros base' }, { rotulo: 'Parceiros comerciais', rota: '/administracao/parceiros' }, ...(partes[2] ? [{ rotulo: 'Parceiro' }] : [])];
    const tela = !pode('visualizar', 'parceiro') ? semPermissao : partes[2] ? <ParceiroDetalhe id={partes[2]} /> : <ListaParceiros />;
    return (
      <Shell rota={caminho} trilha={trilha}>
        <div key={caminho}>{tela}</div>
      </Shell>
    );
  }
  if (partes[0] === 'busca') {
    const q = new URLSearchParams(rota.split('?')[1] ?? '').get('q') ?? '';
    return (
      <Shell rota={caminho} trilha={['Busca']}>
        <ResultadosBusca q={q} />
      </Shell>
    );
  }
  if (caminho.startsWith('/administracao')) {
    const pedida = caminho.split('/')[2];
    const aba = ABAS_ADMIN.find((a) => a.rota === pedida && pode('visualizar', a.entidade)) ?? primeiraAba;
    const sub = aba?.rota ?? '';
    return (
      <Shell rota={caminho} trilha={['Administração', aba?.rotulo ?? '']}>
        <TelaAdministracao sub={sub} />
      </Shell>
    );
  }
  if (partes[0] === 'lixeira') {
    const podeLixeira = ['oportunidade', 'proposta', 'pedido', 'ordem'].some((e) => pode('ver_lixeira', e));
    return (
      <Shell rota={caminho} trilha={['Administração', 'Lixeira']}>
        {podeLixeira ? <TelaLixeira consultaInicial={new URLSearchParams(rota.split('?')[1] ?? '')} /> : semPermissao}
      </Shell>
    );
  }
  if (caminho.startsWith('/auditoria')) {
    return (
      <Shell rota={caminho} trilha={['Administração', 'Auditoria']}>
        <TelaAuditoria rota={rota} />
      </Shell>
    );
  }
  return (
    <Shell rota={caminho} trilha={['Minha conta']}>
      <TelaMinhaConta />
    </Shell>
  );
}
